import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import { DisbursementMethod, IPaymentSet, IPayout, IPayPeriod, PAYOUT_STATUS_DISPLAY_ORDER, PayoutApprovalStatus, PayoutStatus } from 'types';

export interface IReviewPaymentSet {
  id: string;
  net: number;
  sentBy: string;
  // Payout counts per status in display order; statuses with no payouts are left out.
  statuses: { status: PayoutStatus; count: number }[];
}

export interface IReviewDay {
  date: string;
  // True for a business day after today in the site's timezone: nothing could have been paid yet.
  isUpcoming: boolean;
  paymentSets: IReviewPaymentSet[];
}

// A listed payment set carries its payout counts in `totals.status`, not the payouts themselves.
const toReviewPaymentSet = (paymentSet: IPaymentSet): IReviewPaymentSet => ({
  id: paymentSet.id,
  net: paymentSet.totals?.net ?? 0,
  sentBy: paymentSet.createdBy?.name ?? '',
  statuses: PAYOUT_STATUS_DISPLAY_ORDER.flatMap((status) => {
    const count = paymentSet.totals?.status?.find((total) => total.type === status)?.count;
    return count ? [{ status, count }] : [];
  }),
});

/**
 * One row per business day in the pay period, in date order, with the day's payment sets grouped under it.
 * Days with no payments still get a row so the operator sees every day the close is about to lock.
 *
 * @param today - YYYY-MM-DD in the site's timezone; days after it are flagged upcoming rather than empty.
 */
export const buildReviewDays = (payPeriod: IPayPeriod, paymentSets: IPaymentSet[], today: string): IReviewDay[] => {
  const setsByDate = groupBy(paymentSets, (paymentSet) => dayjs(paymentSet.paymentDate).format('YYYY-MM-DD'));

  const days: IReviewDay[] = [];
  for (let day = dayjs(payPeriod.start); !day.isAfter(payPeriod.end, 'day'); day = day.add(1, 'day')) {
    const date = day.format('YYYY-MM-DD');
    days.push({ date, isUpcoming: day.isAfter(today, 'day'), paymentSets: (setsByDate[date] || []).map(toReviewPaymentSet) });
  }

  return days;
};

// Splits the period's open payouts into those awaiting review, which block the close, and the undelivered instant ones.
export const groupOutstandingPayouts = (payouts: IPayout[]) =>
  payouts.reduce<{ reviewRequired: IPayout[]; undelivered: IPayout[] }>(
    (groups, payout) => {
      if (payout.approvalCheck?.status === PayoutApprovalStatus.REVIEW_REQUIRED) groups.reviewRequired.push(payout);
      else if (payout.disbursementMethod === DisbursementMethod.INSTANT) groups.undelivered.push(payout);
      return groups;
    },
    { reviewRequired: [], undelivered: [] }
  );
